import React, { useMemo } from 'react';

/**
 * Variant pickers laid out as pill swatches per attribute (Size, Color, …).
 *
 * For each option key we render its unique values. Tapping a value
 * narrows down — we pick the first variant that matches the chosen
 * value AND the other already-selected attributes. Values with no
 * matching variant under the current selection are dimmed + struck-
 * through so the shopper sees what's available.
 *
 * Props:
 *   variants            — array of `{ _id, attributes: {...}, ... }`
 *   options             — preferred ordering of attribute keys
 *   selectedVariantId   — currently chosen variant id
 *   onSelect(variantId) — fired when the shopper picks a new value
 *   accent              — brand color for the active pill
 */
export default function VariantSwatches({ variants, options, selectedVariantId, onSelect, accent }) {
  const valid = (variants || []).filter((v) => v && v._id);

  // Discover attribute keys from the product `options` array first;
  // fall back to whatever keys the variants actually carry.
  const optionKeys = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) return options;
    const set = new Set();
    valid.forEach((v) => Object.keys(v.attributes || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [options, valid]);

  // Current variant's attributes are the source of truth for what's selected.
  const selectedVariant = valid.find((v) => String(v._id) === String(selectedVariantId)) || valid[0];
  const selectedAttrs = (selectedVariant?.attributes) || {};

  const valuesPerKey = useMemo(() => {
    const map = {};
    optionKeys.forEach((k) => { map[k] = new Set(); });
    valid.forEach((v) => {
      const attrs = v.attributes || {};
      Object.keys(attrs).forEach((k) => {
        if (!map[k]) map[k] = new Set();
        map[k].add(attrs[k]);
      });
    });
    const out = {};
    Object.keys(map).forEach((k) => { out[k] = Array.from(map[k]); });
    return out;
  }, [optionKeys, valid]);

  // For a given (key, value) pair, find the best variant that matches
  // when we honor the other already-selected attribute values too.
  const findVariantFor = (key, value) => {
    const want = { ...selectedAttrs, [key]: value };
    const exact = valid.find((v) => optionKeys.every((k) => (v.attributes || {})[k] === want[k]));
    if (exact) return exact;
    // Loosen: at minimum the chosen (key,value) must match.
    return valid.find((v) => (v.attributes || {})[key] === value);
  };

  const isValueReachable = (key, value) => !!findVariantFor(key, value);

  if (optionKeys.length === 0) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      {optionKeys.map((key) => {
        const values = valuesPerKey[key] || [];
        if (values.length === 0) return null;
        return (
          <div key={key} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
              {key}
              {selectedAttrs[key] && (
                <span style={{ color: '#0f172a', marginLeft: 8, fontWeight: 600 }}>
                  {selectedAttrs[key]}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {values.map((value) => {
                const active = selectedAttrs[key] === value;
                const reachable = isValueReachable(key, value);
                return (
                  <button
                    key={value}
                    type='button'
                    onClick={() => {
                      const next = findVariantFor(key, value);
                      if (next) onSelect(next._id);
                    }}
                    disabled={!reachable}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      background: active ? accent : '#fff',
                      color: active ? '#fff' : (reachable ? '#0f172a' : '#94a3b8'),
                      border: `1px solid ${active ? accent : '#e2e8f0'}`,
                      cursor: reachable ? 'pointer' : 'not-allowed',
                      opacity: reachable ? 1 : 0.5,
                      textDecoration: !reachable ? 'line-through' : 'none',
                      transition: 'background 120ms ease, color 120ms ease',
                    }}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
